import { logStartUsage } from 'owa-analytics-start';
import type OwaUserConfiguration from 'owa-service/lib/contract/OwaUserConfiguration';
import { escapePersistenceId } from './escapePersistenceId';

/**
 * The persistence id prefix that is used for the default account in OWA
 */
const defaultAccountPersistenceIdPrefix = 'DEFDEFDE-FDEF-DEFD-EFDE-FDEFDEFDEFDE';

/**
 * OWA can have accounts open in multiple tabs, we need to make sure that we have
 * a unique persistence id for each tab. We use the user's puid and tenant guid, or
 * email address (when puid and tenant id is not available) to create a unique
 * persistence id suffix.
 * @param userConfiguration The owa user configuration associated with the default account
 */
function getPersistenceIdForDefaultAccountImpl(userConfiguration: OwaUserConfiguration): string {
    const tenant = userConfiguration.SessionSettings?.ExternalDirectoryTenantGuid;
    const puid = userConfiguration.SessionSettings?.UserPuid;
    const mailboxGuid = userConfiguration.SessionSettings?.MailboxGuid;
    /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
     * We are getting the persistend if for the default account, this happens during startup and we need to use the passed in userConfiguration. */
    if (!!userConfiguration?.SessionSettings?.IsExplicitLogon) {
        if (!!tenant && !!mailboxGuid) {
            // explicit logon accounts can return the same PUID as the user account, in this
            // case we want to use the mailbox guid to differentiate the accounts
            return `${defaultAccountPersistenceIdPrefix}-${mailboxGuid}@${tenant}`;
        }

        // We did not get the suffix using the PUID and tenant guid, we want to log that
        /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param, owa-custom-rules/telemetry-event-name-format -- (https://aka.ms/OWALintWiki)
         * BASELINE: DO NOT COPY/PASTE!
         * undefined
         *	> Argument at index 1 for logStartUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo
         (https://aka.ms/OWALintWiki)
                 * Baseline. Dev touching this file must update to pass the active MailboxInfo; Docs: https://aka.ms/owa-analytics-mailboxinfo
                 * undefined
                 *	> MailboxInfo must be passed into the function to identify the account.
                 (https://aka.ms/OWALintWiki)
                         * Baseline. DO NOT COPY AND PASTE! Grandfathering existing event names; Devs who touch this file should update Event Names to follow Outlook Telemetry Naming Standards
                         *	> Invalid Outlook telemetry EventName "InitOwaAccount-MissingMbxGuidOrTenantGuid": must contain only letters, numbers, or underscores. */
        logStartUsage('InitOwaAccount-MissingMbxGuidOrTenantGuid', undefined, {
            tenantType: typeof tenant,
            mbxGuidType: typeof mailboxGuid,
            puidType: typeof puid,
        });
    }

    // Because we are in OWA we know we only connect to M365 sources and can used the tenant
    // guid and user PUID to identity the account. This will be stable with the possible exception
    // of a cloud cache account that is deleted and recreated, which should be infrequent and
    // we can tolerate those id changing sometimes.
    if (!!tenant && !!puid) {
        return `${defaultAccountPersistenceIdPrefix}-${puid}@${tenant}`;
    }

    // Fallback to using the user email address
    const userEmailAddress = userConfiguration.SessionSettings?.UserEmailAddress;
    const hasSuffix = !!userEmailAddress;

    // We did not get the suffix using the PUID and tenant guid, we want to log that
    /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param, owa-custom-rules/telemetry-event-name-format -- (https://aka.ms/OWALintWiki)
     * BASELINE: DO NOT COPY/PASTE!
     * undefined
     *	> Argument at index 1 for logStartUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo
     (https://aka.ms/OWALintWiki)
         * Baseline. Dev touching this file must update to pass the active MailboxInfo; Docs: https://aka.ms/owa-analytics-mailboxinfo
         * undefined
         *	> MailboxInfo must be passed into the function to identify the account.
         (https://aka.ms/OWALintWiki)
             * Baseline. DO NOT COPY AND PASTE! Grandfathering existing event names; Devs who touch this file should update Event Names to follow Outlook Telemetry Naming Standards
             *	> Invalid Outlook telemetry EventName "InitOwaAccount-MissingPuidOrTenantGuid": must contain only letters, numbers, or underscores. */
    logStartUsage('InitOwaAccount-MissingPuidOrTenantGuid', undefined, {
        hasSuffix,
        tenantType: typeof tenant,
        puidType: typeof puid,
        emailType: typeof userEmailAddress,
    });

    return hasSuffix
        ? `${defaultAccountPersistenceIdPrefix}-${userEmailAddress}`
        : defaultAccountPersistenceIdPrefix;
}

export function getPersistenceIdForDefaultAccount(userConfiguration: OwaUserConfiguration): string {
    const original = getPersistenceIdForDefaultAccountImpl(userConfiguration);
    const escaped = escapePersistenceId(original);
    if (original != escaped) {
        /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param, owa-custom-rules/telemetry-event-name-format -- (https://aka.ms/OWALintWiki)
         * BASELINE: DO NOT COPY/PASTE!
         * undefined
         *	> Argument at index 1 for logStartUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo
         (https://aka.ms/OWALintWiki)
                 * Baseline. Dev touching this file must update to pass the active MailboxInfo; Docs: https://aka.ms/owa-analytics-mailboxinfo
                 * undefined
                 *	> MailboxInfo must be passed into the function to identify the account.
                 (https://aka.ms/OWALintWiki)
                         * Baseline. DO NOT COPY AND PASTE! Grandfathering existing event names; Devs who touch this file should update Event Names to follow Outlook Telemetry Naming Standards
                         *	> Invalid Outlook telemetry EventName "InitOwaAccount-PersistenceId-Escaped": must contain only letters, numbers, or underscores. */
        logStartUsage('InitOwaAccount-PersistenceId-Escaped', undefined);
    }

    return escaped;
}
